// src/services/socioService.js

import api, { publicApi, publicApiFormData } from './api';

/**
 * 👥 Servicio de Gestión de Socios
 * Maneja todas las operaciones CRUD de socios y sus relaciones
 * 
 * PÚBLICO (sin autenticación):
 * - Crear socio (POST /socios/)
 * - Listar socios (GET /socios/)
 * - Crear formaciones, estudios, puestos, publicaciones
 * 
 * PROTEGIDO (con autenticación):
 * - Actualizar socio
 * - Eliminar socio
 * - Ver detalles de un socio específico
 */

const socioService = {
  // Usa cliente autenticado cuando existe token, y público en caso contrario.
  _getHttpClient: () => {
    const token = localStorage.getItem('token');
    return token ? api : publicApi;
  },

  // ========================================
  // 🌐 MÉTODOS PÚBLICOS (sin autenticación)
  // ========================================

  /**
   * 📋 Obtener todos los socios (PÚBLICO)
   * @returns {Promise}
   */
  obtenerSocios: async () => {
    try {
      const response = await publicApi.get('/socios/');
      return response.data;
    } catch (error) {
      console.error('Error al obtener socios:', error.response?.data);
      throw new Error('Error al obtener la lista de socios');
    }
  },

  /**
   * 🔎 Obtener datos públicos de un socio por ID (para QR)
   * @param {string} socioId - UUID del socio
   * @returns {Promise}
   */
  obtenerSocioPublicoPorId: async (socioId) => {
    try {
      const response = await publicApi.get(`/socios/${socioId}/public/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener socio público:', error.response?.data);
      throw new Error('No se pudo cargar la información pública del socio');
    }
  },

  /**
   * 📝 Crear nuevo socio con foto (PÚBLICO)
   * @param {Object} socioData - Datos personales del socio
   * @param {File} fotoFile - Archivo de foto (opcional)
   * @returns {Promise} - Promesa con el socio creado
   */
  crearSocioConFoto: async (socioData, fotoFile = null) => {
    try {
      console.log('📝 Creando socio...');
      console.log('Datos:', socioData);
      console.log('Foto:', fotoFile ? 'Sí' : 'No');

      if (fotoFile) {
        // Con foto: usar FormData
        const formData = new FormData();
        
        Object.keys(socioData).forEach(key => {
          if (socioData[key] !== null && socioData[key] !== undefined && socioData[key] !== '') {
            formData.append(key, socioData[key]);
          }
        });
        
        formData.append('avatar', fotoFile);
        
        console.log('📤 Enviando con FormData (publicApiFormData)');
        const response = await publicApiFormData.post('/socios/', formData);
        return response.data;
      } else {
        // Sin foto: usar JSON
        console.log('📤 Enviando con JSON (publicApi)');
        const response = await publicApi.post('/socios/', socioData);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Error en crearSocioConFoto:', error.response?.data);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.errors ||
        'Error al crear socio'
      );
    }
  },

  /**
   * 📝 Crear nuevo socio sin foto (mantener compatibilidad)
   * @param {Object} socioData - Datos personales del socio
   * @returns {Promise} - Promesa con el socio creado
   */
  crearSocio: async (socioData) => {
    return socioService.crearSocioConFoto(socioData, null);
  },

  /**
   * 📚 Crear formación académica para un socio (PÚBLICO)
   * @param {number} socioId - ID del socio
   * @param {Object} formacionData - Datos de la formación
   * @returns {Promise}
   */
  crearFormacionAcademica: async (socioId, formacionData) => {
    try {
      const response = await socioService._getHttpClient().post(
        `/socios/${socioId}/formacion-academica/`,
        formacionData
      );
      return response.data;
    } catch (error) {
      console.error('Error en crearFormacionAcademica:', error.response?.data);
      throw new Error('Error al crear formación académica');
    }
  },

  /**
   * 📖 Crear estudio relacionado para un socio (PÚBLICO)
   * @param {number} socioId - ID del socio
   * @param {Object} estudioData - Datos del estudio
   * @returns {Promise}
   */
  crearEstudio: async (socioId, estudioData) => {
    try {
      const response = await socioService._getHttpClient().post(
        `/socios/${socioId}/estudios/`,
        estudioData
      );
      return response.data;
    } catch (error) {
      console.error('Error en crearEstudio:', error.response?.data);
      throw new Error('Error al crear estudio');
    }
  },

  /**
   * 📄 Crear publicación para un socio (PÚBLICO)
   * @param {number} socioId - ID del socio
   * @param {Object} publicacionData - Datos de la publicación
   * @returns {Promise}
   */
  crearPublicacion: async (socioId, publicacionData) => {
    try {
      const response = await socioService._getHttpClient().post(
        `/socios/${socioId}/publicaciones/`,
        publicacionData
      );
      return response.data;
    } catch (error) {
      console.error('Error en crearPublicacion:', error.response?.data);
      throw new Error('Error al crear publicación');
    }
  },

  /**
   * 💼 Crear puesto desempeñado para un socio (PÚBLICO)
   * @param {number} socioId - ID del socio
   * @param {Object} puestoData - Datos del puesto
   * @returns {Promise}
   */
  crearPuesto: async (socioId, puestoData) => {
    try {
      const response = await socioService._getHttpClient().post(
        `/socios/${socioId}/puestos/`,
        puestoData
      );
      return response.data;
    } catch (error) {
      console.error('Error en crearPuesto:', error.response?.data);
      throw new Error('Error al crear puesto');
    }
  },

  /**
   * 🔄 Registrar socio completo con foto (con todas sus relaciones)
   * Esta función maneja todo el proceso de registro en secuencia
   * @param {Object} datosCompletos - Objeto con todos los datos del formulario
   * @param {File} fotoFile - Archivo de foto (opcional)
   * @returns {Promise}
   */
  registrarSocioCompleto: async (datosCompletos, fotoFile = null) => {
    try {
      const {
        datosPersonales,
        formaciones,
        estudios,
        publicaciones,
        puestos,
      } = datosCompletos;

      console.log('📝 Iniciando registro de socio...');
      console.log('Datos personales:', datosPersonales);
      console.log('Foto:', fotoFile ? 'Sí' : 'No');

      // 1. Crear el socio primero (con o sin foto)
      const socioCreado = await socioService.crearSocioConFoto(
        datosPersonales,
        fotoFile
      );
      
      console.log('✅ Socio creado:', socioCreado);

      const socioId = socioCreado.id || socioCreado.socio?.id;

      if (!socioId) {
        throw new Error('No se pudo obtener el ID del socio creado');
      }

      console.log(`🆔 ID del socio: ${socioId}`);

      // 2. Crear formaciones académicas (si existen y tienen datos)
      const formacionesValidas = formaciones.filter(
        (f) => f.institucion && f.titulo_acad
      );
      
      console.log(`📚 Creando ${formacionesValidas.length} formaciones...`);
      
      const formacionesPromises = formacionesValidas.map((formacion) =>
        socioService.crearFormacionAcademica(socioId, formacion)
      );

      // 3. Crear estudios (si existen y tienen datos)
      const estudiosValidos = estudios.filter(
        (e) => e.institucion && e.nombre_curso
      );
      
      console.log(`📖 Creando ${estudiosValidos.length} estudios...`);
      
      const estudiosPromises = estudiosValidos.map((estudio) =>
        socioService.crearEstudio(socioId, estudio)
      );

      // 4. Crear publicaciones (si existen y tienen datos)
      const publicacionesValidas = publicaciones.filter(
        (p) => p.titulo && p.revista
      );
      
      console.log(`📄 Creando ${publicacionesValidas.length} publicaciones...`);
      
      const publicacionesPromises = publicacionesValidas.map((publicacion) =>
        socioService.crearPublicacion(socioId, publicacion)
      );

      // 5. Crear puestos (si existen y tienen datos)
      const puestosValidos = puestos.filter(
        (p) => p.puesto && p.institucion_empresa
      );
      
      console.log(`💼 Creando ${puestosValidos.length} puestos...`);
      
      const puestosPromises = puestosValidos.map((puesto) =>
        socioService.crearPuesto(socioId, puesto)
      );

      // Ejecutar todas las promesas en paralelo
      await Promise.all([
        ...formacionesPromises,
        ...estudiosPromises,
        ...publicacionesPromises,
        ...puestosPromises,
      ]);

      console.log('✅ Registro completo exitoso');

      return {
        success: true,
        socio: socioCreado,
        message: 'Socio registrado exitosamente',
      };
    } catch (error) {
      console.error('❌ Error en registro completo:', error);
      throw error;
    }
  },

  // ========================================
  // 🔐 MÉTODOS AUTENTICADOS (para Dashboard)
  // ========================================

  /**
   * 👤 Obtener un socio específico (requiere autenticación)
   * @param {number} socioId - ID del socio
   * @returns {Promise}
   */
  obtenerSocioPorId: async (socioId) => {
    try {
      const response = await api.get(`/socios/${socioId}/`);
      return response.data;
    } catch (error) {
      throw new Error('Error al obtener socio');
    }
  },

  /**
   * ✏️ Actualizar socio (requiere autenticación)
   * @param {number} socioId - ID del socio
   * @param {Object} socioData - Datos a actualizar
   * @param {File} fotoFile - Archivo de foto (opcional)
   * @returns {Promise}
   */
  actualizarSocio: async (socioId, socioData, fotoFile = null) => {
    try {
      if (fotoFile) {
        const formData = new FormData();
        
        Object.keys(socioData).forEach(key => {
          if (socioData[key] !== null && socioData[key] !== undefined && socioData[key] !== '') {
            formData.append(key, socioData[key]);
          }
        });
        
        formData.append('avatar', fotoFile);
        
        const response = await api.patch(`/socios/${socioId}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } else {
        const response = await api.patch(`/socios/${socioId}/`, socioData);
        return response.data;
      }
    } catch (error) {
      throw new Error('Error al actualizar socio');
    }
  },

  /**
   * 🗑️ Eliminar socio (requiere autenticación)
   * @param {number} socioId - ID del socio
   * @returns {Promise}
   */
  eliminarSocio: async (socioId) => {
    try {
      const response = await api.delete(`/socios/${socioId}/`);
      return response.data;
    } catch (error) {
      throw new Error('Error al eliminar socio');
    }
  },
};

export default socioService;
